import React from 'react';
import { ShoppingHistoryEntry, ShoppingItem } from '../../../lib/schemas';
import { Calendar, Store, CreditCard, ChevronDown, ChevronUp, MapPin, ExternalLink, Tag, PlusCircle, TrendingDown, TrendingUp, Minus, Clock } from 'lucide-react';
import { useCartAssistant, useCartAssistantInput } from "../../../lib/cartAssistant/CartAssistantProvider";
import { liveState, useStore } from '../../../lib/store';
import { useTranslation } from 'react-i18next';
import { CURRENCIES } from '../../../../constants';
import { SmartImage } from '../../../../components/common/SmartImage';

interface DetailedShoppingTripCardProps {
  entry: ShoppingHistoryEntry & {
    items: Array<ShoppingItem & {
      priceStatus?: 'up' | 'down' | 'stable';
      usualPrice?: number;
    }>;
  };
  activeCurrencySymbol?: string;
  onPreviewImage?: (url: string) => void;
}

export const DetailedShoppingTripCard: React.FC<{ props: DetailedShoppingTripCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t, i18n } = useTranslation();
  const { userProfile, currency } = useStore();
  const activeCurrencySymbol = React.useMemo(() => {
    return CURRENCIES.find(c => c.code === (currency || userProfile?.settings?.interface?.currency))?.symbol || props?.activeCurrencySymbol || '$';
  }, [userProfile?.settings?.interface?.currency, props?.activeCurrencySymbol]);

  const { entry, onPreviewImage } = props || {};
  const [isExpanded, setIsExpanded] = React.useState(false);
  const assistantInput = useCartAssistantInput();
  const { thread, cancelRun, isIdle } = useCartAssistant();
  const [pendingText, setPendingText] = React.useState<string | null>(null);

  // RESILIENCE: Check if thread is stuck waiting for tool calls
  const isStuck = React.useMemo(() => {
    const threadAny = thread as any;
    return threadAny?.thread?.status === 'requires_action' || 
           (threadAny?.pendingToolCallIds && threadAny.pendingToolCallIds.length > 0) ||
           (!isIdle && !threadAny?.streaming?.isActive);
  }, [thread, isIdle]);

  React.useEffect(() => {
    const fireSubmit = async (isRetry = false) => {
      if (pendingText && assistantInput.value === pendingText && !assistantInput.isDisabled) {
        try {
          // PROACTIVE RECOVERY: If the thread is stuck waiting for tool calls from a previous run,
          // submit() will throw 400. We cancel the existing run here to ensure success.
          if (isStuck) {
            console.warn("[CartAssistant] [DetailedShoppingTripCard] Thread state is stuck. Attempting to cancel run...");
            if (cancelRun) {
              try {
                await cancelRun();
                console.log("[CartAssistant] [DetailedShoppingTripCard] Stuck run cancelled proactively.");
                await new Promise(resolve => setTimeout(resolve, 1000));
                // Re-sync value after cancel
                assistantInput.setValue(pendingText);
                await new Promise(resolve => setTimeout(resolve, 300));
              } catch (err) {
                console.error("[CartAssistant] [DetailedShoppingTripCard] Proactive cancel failed:", err);
              }
            }
          }

          console.log("[CartAssistant] [DetailedShoppingTripCard] API SUBMIT INITIATED", { isRetry });

          await assistantInput.submit();
          setPendingText(null);
        } catch (err: any) {
          const errorMsg = err?.message || String(err);
          const is400 = errorMsg.includes('400') || 
                       errorMsg.includes('MISSING_RESULTS') || 
                       err?.status === 400 ||
                       err?.code === 'MISSING_RESULTS';
          
          console.error(`[CartAssistant] [DetailedShoppingTripCard] Submission failed (isRetry: ${isRetry}):`, errorMsg);
          
          if (is400 && !isRetry) {
             const isSdkOutOfSync = isIdle;
             
             if (!isSdkOutOfSync) {
               console.warn("[CartAssistant] [DetailedShoppingTripCard] 400 MISSING_RESULTS detected. Attempting auto-recovery retry...");
               try {
                 if (cancelRun) {
                   await cancelRun();
                   console.log("[CartAssistant] [DetailedShoppingTripCard] Stuck run cancelled on error catch. Retrying...");
                   await new Promise(resolve => setTimeout(resolve, 1500));
                   // Re-apply the value before retry
                   assistantInput.setValue(pendingText);
                   await new Promise(resolve => setTimeout(resolve, 500));
                   // AUTO-RETRY
                   return await fireSubmit(true);
                 }
               } catch (recoveryErr) {
                 console.error("[CartAssistant] [DetailedShoppingTripCard] Recovery retry failed:", recoveryErr);
               }
             } else {
               console.warn("[CartAssistant] [DetailedShoppingTripCard] SDK state desync detected (Idle but backend waiting for tool). Skipping futile retry.");
             }
          }
          setPendingText(null);
        }
      }
    };

    fireSubmit();
  }, [pendingText, assistantInput.value, assistantInput.isDisabled, assistantInput.submit, thread, cancelRun, isStuck]);

  const handleRepeatTrip = () => {
    if (!assistantInput.isDisabled && entry) {
      const text = t('chat.repeat_trip_intent', { 
        store: entry.storeInfo?.name || t('common.local_store'), 
        date: new Date(entry.date).toLocaleDateString(i18n.language),
        id: entry.id
      });
      assistantInput.setValue(text);
      setPendingText(text);
    }
  };

  if (!entry || !entry.date) {
    if (isStreaming) {
      return (
        <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center animate-pulse">
           <p className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">{t('common.loading')}</p>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
         <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('chat.incomplete_data')}</p>
      </div>
    );
  }

  const date = new Date(entry.date);
  const formattedDate = date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
  const formattedTime = date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  const items = Array.isArray(entry?.items) ? entry.items : [];
  const totalCost = typeof entry?.totalCost === 'number' ? entry.totalCost : 0;

  // Option 2: Category Breakdown calculation
  const categoryTotals = items.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + (item.price * item.quantity);
    return acc;
  }, {} as Record<string, number>);

  const sortedCategories = Object.entries(categoryTotals)
    .sort((a, b) => b[1] - a[1])
    .map(([name, amount]) => {
      const catInfo = liveState.categories.find(c => c.name === name);
      const displayName = catInfo?.translationKey ? t(catInfo.translationKey) : name;
      return {
        name: displayName,
        amount,
        percent: (amount / totalCost) * 100,
        color: catInfo?.color || '#94a3b8'
      };
    });

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      {/* Header */}
      <div className="p-6 bg-emerald-500 text-white flex justify-between items-start gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-sm shadow-inner text-white shrink-0">
            <Store size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-emerald-100 mb-1 opacity-80 leading-none">{t('chat.historical_visit')}</h4>
            <p className="text-base font-black text-white leading-tight break-words">
              {entry.storeInfo?.name || t('common.local_store')}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
           <p className="text-2xl font-black text-white leading-none mb-1">{activeCurrencySymbol}{totalCost.toFixed(2)}</p>
           <p className="text-[9px] font-black text-emerald-100 uppercase tracking-widest opacity-80">{t('chat.items_total_count', { count: items.length })}</p>
        </div>
      </div>

      <div className="p-6 space-y-6 bg-white">
        {/* Date & Location Row */}
        <div className="flex items-center justify-between text-[11px] font-bold text-[#1a2b3c]">
           <div className="flex items-center gap-2 text-gray-400">
             <Calendar size={14} />
             <span>{formattedDate} • {formattedTime}</span>
           </div>
           {entry.storeInfo?.address && (
              <a 
                href={entry.storeInfo.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(entry.storeInfo.address)}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-emerald-600 hover:text-emerald-700 transition-colors bg-emerald-50 px-2 py-1 rounded-lg"
              >
                <MapPin size={12} />
                <span className="truncate max-w-[100px]">{entry.storeInfo.address}</span>
                <ExternalLink size={10} />
              </a>
           )}
        </div>

        {/* Receipt Visual (Evidence) */}
        {entry.receiptImageUrl && (
          <div className="space-y-2.5">
            <div className="flex justify-between items-center px-1">
              <h5 className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{t('history.view_receipt')}</h5>
              <CreditCard size={10} className="text-gray-300" />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => liveState.triggerAction('VIEW_DIGITAL_RECEIPT', entry)}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#1a2b3c] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-[#2a3b4c] transition-all shadow-lg active:scale-95 border border-white/10"
              >
                <Tag size={12} className="text-emerald-400" />
                {t('history.view_digital_receipt')}
              </button>

              <button
                onClick={() => {
                  if (onPreviewImage) onPreviewImage(entry.receiptImageUrl!);
                  else liveState.triggerAction('VIEW_ORIGINAL_RECEIPT', entry);
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-600 transition-all shadow-lg active:scale-95"
              >
                <Store size={12} />
                {t('history.original_receipt')}
              </button>
            </div>

            <div
              onClick={() => { if (onPreviewImage) onPreviewImage(entry.receiptImageUrl!); }}
              className="relative aspect-[16/9] w-full rounded-2xl overflow-hidden border border-gray-100 cursor-pointer group shadow-inner bg-gray-50 mt-2"
            >
              <SmartImage
                imageUrl={entry.receiptImageUrl}
                alt="Receipt"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <div className="bg-white/20 backdrop-blur-md p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-xl border border-white/20">
                  <ExternalLink size={18} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Option 2: Mini Category Breakdown */}
        {sortedCategories.length > 0 && (
          <div className="space-y-3 bg-gray-50/50 p-4 rounded-3xl border border-gray-100">
            <div className="flex justify-between items-center px-1">
              <h5 className="text-[9px] font-black uppercase text-gray-400 tracking-widest">{t('chat.trip_composition')}</h5>
              <Tag size={10} className="text-gray-300" />
            </div>
            <div className="flex h-2.5 w-full rounded-full overflow-hidden shadow-inner bg-gray-100">
              {sortedCategories.map((cat, i) => (
                <div 
                  key={i}
                  style={{ width: `${cat.percent}%`, backgroundColor: cat.color }}
                  className="h-full first:rounded-l-full last:rounded-r-full transition-all duration-1000"
                  title={`${cat.name}: ${activeCurrencySymbol}${cat.amount.toFixed(2)}`}
                />
              ))}
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {sortedCategories.slice(0, 3).map((cat, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
                  <span className="text-[9px] font-black text-[#1a2b3c] uppercase tracking-tight opacity-70">
                    {cat.name}
                  </span>
                </div>
              ))}
            </div>
              {sortedCategories.length > 3 && (
                <span className="text-[9px] font-black text-gray-300 uppercase tracking-tight">+{sortedCategories.length - 3} {t('common.more')}</span>
              )}
            </div>
          </div>
        )}

        {/* Item List with Delta Markers */}
        <div className="space-y-3">
          {(isExpanded ? items : items.slice(0, 3)).map((item, i) => {
            let imageUrl = item.imageUrl;
            
            // RECOVERY: If image is missing, try to find it in the app's real history state
            if (entry?.id && (!imageUrl || imageUrl.length < 20)) {
              const realTrip = liveState.history.find((h: any) => h.id === entry.id);
              if (realTrip) {
                const realItem = realTrip.items.find((ri: any) => ri.id === item.id || ri.name === item.name);
                if (realItem?.imageUrl) imageUrl = realItem.imageUrl;
              }
            }

            return (
              <div key={i} className="flex justify-between items-center group">
                <div className="flex items-center gap-3 min-w-0">
                  {imageUrl ? (
                    <SmartImage 
                      imageUrl={imageUrl} 
                      alt={item.name} 
                      className="w-8 h-8 rounded-xl object-cover border border-gray-100 shadow-sm" 
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-500 shrink-0 border border-emerald-100/50">
                      <Tag size={14} />
                    </div>
                  )}
                  <div className="min-w-0 py-1">
                    <p className="text-[12px] font-bold text-[#1a2b3c] whitespace-normal group-hover:text-emerald-600 transition-colors leading-snug mb-0.5 break-words">{item.name}</p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-[9px] font-black text-gray-300 uppercase shrink-0">{item.quantity}x</span>
                      
                      {item.shelfLife && (
                        <div className="flex items-center gap-1 text-blue-500">
                          <Clock size={10} className="shrink-0" />
                          <span className="text-[8px] font-black uppercase whitespace-nowrap">{item.shelfLife} {t('product.shelf_life_days')}</span>
                        </div>
                      )}

                      {item.priceStatus === 'down' && (
                        <div className="flex items-center gap-0.5 text-emerald-500 animate-pulse">
                          <TrendingDown size={10} />
                          <span className="text-[8px] font-black uppercase">{t('chat.good_deal')}</span>
                        </div>
                      )}
                      {item.priceStatus === 'up' && (
                        <div className="flex items-center gap-0.5 text-rose-500">
                          <TrendingUp size={10} />
                          <span className="text-[8px] font-black uppercase">{t('chat.price_up')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-black text-[#1a2b3c]">{activeCurrencySymbol}{(item.price * item.quantity).toFixed(2)}</p>
                  {item.usualPrice && (
                    <p className="text-[8px] font-bold text-gray-300 uppercase tracking-tighter">{t('chat.vs_label')} {activeCurrencySymbol}{item.usualPrice.toFixed(2)} {t('common.avg')}</p>
                  )}
                </div>
              </div>
            );
          })}
          
          {items.length > 3 && (
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full mt-2 py-3 text-[9px] font-black text-gray-400 uppercase tracking-widest hover:text-emerald-500 transition-colors flex items-center justify-center gap-2 border-t border-gray-50"
            >
              {isExpanded ? (
                <>{t('chat.collapse_summary')} <ChevronUp size={14} /></>
              ) : (
                <>{t('chat.view_all_items', { count: items.length })} <ChevronDown size={14} /></>
              )}
            </button>
          )}
        </div>

        {/* Action Button */}
        <div className="pt-4 border-t border-gray-100">
           <button 
             onClick={handleRepeatTrip}
             className="w-full bg-[#1a2b3c] text-white py-4 rounded-[1.5rem] font-black text-[11px] uppercase tracking-[0.2em] shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2 hover:bg-[#2a3b4c]"
           >
             <PlusCircle size={16} className="text-emerald-400" /> {t('chat.readd_all_to_basket')}
           </button>
        </div>
      </div>
    </div>
  );
};
